export enum ShipmentStatus {
  CREATED = "CREATED",
  REQUESTED = "REQUESTED",
  ASSIGNED = "ASSIGNED",
  PICKED_UP = "PICKED_UP",
  IN_TRANSIT = "IN_TRANSIT",
  OUT_FOR_DELIVERY = "OUT_FOR_DELIVERY",
  DELIVERED = "DELIVERED",
  CANCELLED = "CANCELLED",
}

export enum EngagementMode {
  PLATFORM = "PLATFORM",
  SUBSCRIBED = "SUBSCRIBED",
  EXPIRED = "EXPIRED",
}

export enum PorterDeliveryTrackingStatus {
  OPEN = "open",
  ACCEPTED = "accepted",
  LIVE = "live",
  ENDED = "ended",
  CANCELLED = "cancelled",
}

export enum VehicleType {
  TWO_WHEELER = "TWO_WHEELER",
  OTHER = "OTHER",
}

export interface Order {
  id: string;
  orderNo: string;
  date: string;
  customerName: string;
  branchName: string;
  deliveryMode: string;
  invoiceInfo: string;
  orderValue: string;
  weight: string;
  dimensions: string;
  status: string;
}

export interface Customer {
  _id: string;
  name: string;
  type: string;
  // Shipment counts
  totalShipmentsCount?: number;
  inTransitShipmentsCount?: number;
  cancelledShipmentsCount?: number;
  totalDeliveredShipments?: number;
  totalUndeliveredShipments?: number;
  // Address information
  shipmentAddress: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface Supplier {
  _id: string;
  name: string;
  // Shipment counts
  totalShipmentsCount: number;
  inTransitShipmentsCount?: number;
  cancelledShipmentsCount?: number;
  totalDeliveredShipments?: number;
  totalUndeliveredShipments?: number;
  // Address information
  supplierAddress: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface Shipment {
  shipmentId: string;
  customerId?: string;
  supplierId: string;
  orderNumbers: string[];
  invoiceNumbers?: string[];
  supplierInvoiceNumbers?: string[];
  totalItems: number;
  totalQuantity: number;
  totalValue: number;
  status: ShipmentStatus;
  pickupDetails: {
    address: {
      streetAddress1: string;
      streetAddress2?: string;
      city: string;
      state: string;
      pincode: string;
      country: string;
      lat: number;
      lng: number;
      contactDetails: {
        name: string;
        phoneNumber: string;
      };
    };
  };
  dropDetails: {
    address?: {
      streetAddress1: string;
      streetAddress2?: string;
      city: string;
      state: string;
      pincode: string;
      country: string;
      lat: number;
      lng: number;
      contactDetails: {
        name: string;
        phoneNumber: string;
      };
    };
    [orderNo: string]: unknown;
  };
  deliveryBooking?: {
    requestId?: string;
    providerOrderId?: string;
    trackingUrl?: string;
    estimatedPickupTime?: Date;
    estimatedDeliveryTime?: number;
    estimatedFare?: {
      currency?: string;
      minorAmount?: number;
    };
    actualFare?: {
      currency?: string;
      minorAmount?: number;
    };
    deliveryInstructions?: string;
    bookedAt?: Date;
  };
  deliveryTracking?: {
    trackingStatus?: PorterDeliveryTrackingStatus;
    currentLocation?: {
      lat?: number;
      lng?: number;
      updatedAt?: Date;
    };
    deliveryAgent?: {
      name?: string;
      phoneNumber?: string;
      alternatePhoneNumber?: string;
      vehicleDetails?: {
        vehicleNumber?: string;
        vehicleType?: string;
      };
    };
    timeline?: {
      assignedToAgentAt?: Date;
      pickedUpAt?: Date;
      deliveredAt?: Date;
      cancelledAt?: Date;
      reopenedAt?: Date;
      bookedAt?: Date;
    };
    lastUpdatedAt?: Date;
  };
  createdAt: Date;
  updatedAt: Date;
  customerIds?: string[];
  deliveryStops?: Record<
    string,
    {
      address:
        | Shipment["dropDetails"]["address"]
        | { address?: Shipment["dropDetails"]["address"] };
      status: "PENDING" | "DELIVERED";
      deliveredAt?: string;
    }
  >;
}

// Common interface for both customer and supplier data in first level cards
export interface FirstLevelEntity {
  _id: string;
  name: string;
  type: string;
  totalShipmentsCount: number;
  totalUndeliveredShipments: number;
  inTransitShipmentsCount?: number;
  cancelledShipmentsCount?: number;
  totalDeliveredShipments?: number;
  engagementMode?: string; 
  address: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
}

// Common interface for second level entities
export interface SecondLevelEntity {
  _id: string;
  name: string;
  totalShipmentsCount: number;
  totalUndeliveredShipments: number;
  inTransitShipmentsCount?: number;
  cancelledShipmentsCount?: number;
  totalDeliveredShipments?: number;
  engagementMode?: string;
  address: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
}
